import React, { useContext, useEffect, useRef } from "react";
import { classNames } from "../../../helpers/classNames";
import dayjs from "dayjs";
import { checkImageByExtention, initialsValue } from "../../../helpers";
import ChatImage from "./ChatImage";
import Image from "../Image";
import Indentification from "../Indentification";
import SocketContext, { useSocket } from "../../../providers/SocketContext";
import { useSelector } from "react-redux";
import TableAction from "../table/TableAction";

const MessageViewer = React.forwardRef(
  (
    {
      messages,
      today,
      setRender = () => {},
      onScroll = () => {},
      chatId,
      unread,
      setUnread = () => {},
      onDeleteMessage = () => {},
    },
    ref
  ) => {
    const { session } = useSelector((state) => state?.authProvider);
    const { socket } = useContext(SocketContext);

    useEffect(() => {
      setRender("mount");
      return () => {
        setRender("unmount");
      };
    }, []);

    useEffect(() => {
      if (!ref?.current) return;
      ref.current.scrollTop = ref.current.scrollHeight;
    }, [ref]);

    const scrollHandler = async (e) => {
      if (
        Math.ceil(e.target.scrollTop) + 20 >=
        e.target.scrollHeight - e.target.clientHeight
      ) {
        setUnread([]);
      }

      const res = await onScroll(e);
      if (res?.docs?.status === 200) {
        e.target.scrollTop = 150;
      }
    };

    useSocket(
      "sendMessageResponse",
      async (res) => {
        const container = ref?.current;
        if (!container || res?.data?.chatId !== chatId) return;

        const isUserNearBottom =
          container.scrollHeight -
            container.scrollTop -
            container.clientHeight <
          40;

        setTimeout(() => {
          if (res?.data?.from === session?.user?._id || isUserNearBottom) {
            container.scrollTop = container.scrollHeight + 30;
          } else {
            setUnread((prev) => [...(prev || []), res?.data]);
          }
        }, 200);
      },
      [session?.user?._id, chatId, ref.current]
    );

    const actionData = (data) => [
      {
        _id: 9.1,
        name: "Delete",
        icon: "fa-regular fa-trash-can",
        standout: true,
        onClick: () => {
          onDeleteMessage(data);
        },
      },
    ];

    useSocket(
      "sendMessageResponse",
      async (res) => {
        if (res?.status !== 200 || !res?.data) return;
        if (res?.data?.chatId === chatId) {
          const container = ref?.current;
          if (!container || res?.data?.chatId !== chatId) return;
          const isUserNearBottom =
            container.scrollHeight -
              container.scrollTop -
              container.clientHeight <
            40;
          if (isUserNearBottom) {
            socket.emit("messageSeenByChatId", { chatId: chatId });
          }
        }
      },
      [chatId, ref.current]
    );

    return (
      <div
        ref={ref}
        className="overflow-y-auto mt-auto flex [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-400"
        onScroll={scrollHandler}
      >
        <div className="flex-1 p-4 space-y-2 mt-auto relative">
          {messages?.map((dateGroup, i) => (
            <div key={i}>
              <div className="flex justify-center">
                <span className="text-sm bg-chestnut-rose text-white px-3 py-1 rounded mb-5">
                  {today === dayjs(dateGroup?.date).format("DD MMM, YYYY")
                    ? "Today"
                    : dayjs(dateGroup?.date).format("DD MMM, YYYY")}
                </span>
              </div>
              <div className="space-y-4">
                {dateGroup?.data?.map((timeGroup, j) => (
                  <div key={j}>
                    <div
                      className={classNames(
                        "flex w-fit gap-2",
                        timeGroup?.authorizedUserId === timeGroup?.sendBy
                          ? "ml-auto"
                          : ""
                      )}
                    >
                      <div className="space-y-1">
                        {timeGroup?.data?.map((msg, k) => (
                          <div
                            key={k}
                            className={classNames(
                              `flex`,
                              msg?.isMe ? "justify-end" : "justify-start"
                            )}
                          >
                            <div
                              className={classNames(
                                "space-y-1 flex flex-col",
                                msg?.attachment?.url
                                  ? "bg-chestnut-rose/30 rounded-lg p-2"
                                  : ""
                              )}
                            >
                              {msg?.attachment?.url && (
                                <div className="w-60 ml-auto rounded-md overflow-hidden">
                                  <ChatImage
                                    src={
                                      checkImageByExtention(
                                        msg?.attachment?.extension
                                      )
                                        ? msg?.attachment?.url
                                        : ""
                                    }
                                    alt={msg?.attachment?.url}
                                    effect={"fade"}
                                  />
                                </div>
                              )}

                              <div
                                className={classNames(
                                  "flex gap-2",
                                  msg?.isMe ? "flex-row-reverse" : ""
                                )}
                              >
                                {!msg?.isMe && (
                                  <div className="rounded-full bg-chestnut-rose/40 w-9 min-w-9 h-9 overflow-hidden">
                                    {msg?.senderImage?.url ? (
                                      <Image
                                        effect={"fade"}
                                        src={msg?.senderImage?.url}
                                        alt={msg?.name}
                                      />
                                    ) : (
                                      <Indentification
                                        initial={initialsValue(msg?.name)}
                                        size="sm"
                                        className="w-full h-full"
                                      />
                                    )}
                                  </div>
                                )}
                                {msg?.text && (
                                  <div className="flex gap-1 items-end relative">
                                    <div
                                      className={`py-3 px-4 rounded-xl ${
                                        msg?.isMe
                                          ? "bg-chestnut-rose text-white after:absolute after:content after:w-0 after:h-0 after:border-t-[16px] after:border-t-transparent after:border-r-[26px] after:border-r-chestnut-rose after:border-b-[12px] after:border-b-transparent after:top-[-7px] after:right-[4px] after:transform after:rotate-[150deg] after:rounded-2xl"
                                          : "bg-white"
                                      } max-w-md`}
                                    >
                                      <p className="text-sm">{msg?.text}</p>
                                    </div>
                                    {msg?.isMe && (
                                      <span className="fa-regular fa-circle-check text-[11px]"></span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {msg?.authorizedUserId === msg?.sendBy &&
                              session?.user?.roleCode === "admin" && (
                                <div className="ml-1">
                                  <TableAction
                                    data={actionData(msg)}
                                    buttonClass={"!bg-transparent shadow-none"}
                                  />
                                </div>
                              )}
                          </div>
                        ))}
                        <div
                          className={classNames(
                            "text-xs text-gray-400 flex items-center",
                            timeGroup?.authorizedUserId === timeGroup?.sendBy
                              ? "justify-end"
                              : "ml-12"
                          )}
                        >
                          {today ===
                          dayjs(dateGroup?.date).format("DD MMM, YYYY") ? (
                            dayjs(timeGroup?.time).format("hh:mm A")
                          ) : (
                            <div className="flex items-center gap-3 w-fit">
                              <span>
                                {dayjs(timeGroup?.time).format("DD MMM, YYYY")}
                              </span>
                              <span>
                                {dayjs(timeGroup?.time).format("hh:mm A")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

MessageViewer.displayName = "MessageViewer";
export default MessageViewer;
